'use client';

import React from 'react';
import MaterialIcons from '../MaterialIcons/materialIcons';
import { EditIcon } from '../Icons/Icons';
import { ProfileAvatar } from './ProfileAvatar';

export type ProfileSection = 'learning-dashboard' | 'history' | 'enrollments' | 'achievements' | 'bookmarks';

interface ProfileSidebarProps {
  userName?: string;
  profileImage?: string;
  profileImageCacheBust?: number;
  organization?: string;
  activeSection?: ProfileSection;
  onSectionChange: (section: ProfileSection) => void;
  onEditClick?: () => void;
}

/** Profile section nav items - exported for use in header menu when on profile (mobile) */
export const PROFILE_SECTION_NAV_ITEMS: { label: string; section: ProfileSection }[] = [
  { label: 'Learning Dashboard', section: 'learning-dashboard' },
  { label: 'History', section: 'history' },
  { label: 'Enrollments', section: 'enrollments' },
  { label: 'Achievements', section: 'achievements' },
  { label: 'Bookmarks', section: 'bookmarks' },
];

export function ProfileSidebar({
  userName = 'User',
  profileImage,
  profileImageCacheBust,
  organization,
  activeSection,
  onSectionChange,
  onEditClick,
}: ProfileSidebarProps) {
  // Calculate initials from userName
  const getInitials = (name: string): string => {
    if (!name || name === 'User') return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0).toUpperCase()}${parts[parts.length - 1].charAt(0).toUpperCase()}`;
    }
    return name.charAt(0).toUpperCase() || 'U';
  };

  const userInitials = getInitials(userName);

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    }
  };

  return (
    <div className="hidden lg:block w-[180px] lg:w-[320px] flex-shrink-0">
      {/* Profile Header - hidden on mobile and tablet, visible from lg */}
      <div className="hidden lg:flex items-center gap-4 p-4 bg-surface-3">
        {/* Profile Icon - always has gradient background, shows initials on error */}
        <ProfileAvatar
          profilePicture={profileImage ?? null}
          userInitials={userInitials}
          cacheBust={profileImageCacheBust}
          size="md"
        />

        {/* Center Content */}
        <div className="flex-1 flex flex-col justify-center min-w-0 ">
          <div className="flex items-center justify-between gap-2">
            <h5 className="headers-h5 truncate min-w-0 flex-1">{userName}</h5>

            {/* Edit Icon */}
            <button 
              className="flex-shrink-0 cursor-pointer text-text-default-medium hover:text-text-default-strong transition-colors" 
              aria-label="Edit profile"
              onClick={handleEditClick}
            >
              <EditIcon className="w-5 h-6 text-icon-subtle" />
            </button>
          </div>

          {organization && (
            <p className="cs-caption-utility">Org: {organization}</p>
          )}
        </div>
      </div>


      {/* Navigation Items */}
      <nav className="flex flex-col bg-surface-2">
        {PROFILE_SECTION_NAV_ITEMS.map((item) => {
          const isActive = activeSection !== undefined && activeSection === item.section;
          return (
            <button
              key={item.section}
              onClick={() => onSectionChange(item.section)}
              className={`
                w-full
                flex
                items-center
                gap-3
                lg:gap-4
                py-2
                px-3
                lg:px-4
                border-l-2
                cs-s-body
                transition-all
                duration-200
                text-left
                touch-manipulation
                cursor-pointer
                ${isActive
                  ? 'border-amethyst-accent text-text-default-strong bg-surface-1'
                  : 'border-transparent hover:border-amethyst-accent hover:text-text-default-strong hover:bg-surface-1 active:bg-surface-1'
                }
              `}
            >
              {item.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

